import { ChangeDetectorRef, Component, ElementRef, forwardRef, Input, NgZone, SimpleChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-thousand-separator-input',
  templateUrl: './thousand-seperator-input.component.html',
  styleUrls: ['./thousand-seperator-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ThousandSeperatorInputComponent),
      multi: true
    }
  ]
})
export class ThousandSeperatorInputComponent implements ControlValueAccessor {
  @Input('placeholder') placeholder: string = 'Enter here';
  @Input() requiredMark: boolean = false;
  @Input() isInValid: boolean = false;
  @Input() disabled: boolean = false;
  @Input() isHeader: boolean = false;
  @Input() controlValue: any;
  @Input() id: string = '';
  @Input() ariaDescribedby: string | null = null;
  @Input() ariaRequired: string | null = null;

  @ViewChild('numberInput') numberInput!: ElementRef<HTMLInputElement>;

  private rawValue: string = '';
  formattedValue: string = '';

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};
  showSteppers: boolean = false;
  step: number = 1;
  isButtonClicked: boolean = false;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone
  ) {}

  writeValue(value: any): void {
    if (value !== null && value !== undefined) {
      this.rawValue = value.toString();
      this.formattedValue = this.formatWithCommas(this.rawValue);
    } else {
      this.rawValue = '';
      this.formattedValue = '';
    }

    this.ngZone.run(() => {
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['controlValue'] && changes['controlValue'].currentValue !== undefined && changes['controlValue'].currentValue !== null && changes['controlValue'].currentValue !== this.rawValue) {
      this.writeValue(changes['controlValue'].currentValue);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    let inputValue = inputElement.value.replace(/,/g, '');

    // Validate input: only allow numbers, one optional minus sign, and one optional decimal point
    const validNumberPattern = /^-?\d*\.?\d*$/;
    if (!validNumberPattern.test(inputValue)) {
      // If input is invalid, reset to last valid rawValue
      inputElement.value = this.formattedValue;
      return;
    }

    this.rawValue = inputValue;

    // Format with commas for display
    this.formattedValue = this.formatWithCommas(this.rawValue);

    // Emit raw value to the form control
    if (this.rawValue) {
      const numericValue = this.rawValue.includes('.')
        ? parseFloat(this.rawValue)
        : parseInt(this.rawValue);
      if (!isNaN(numericValue)) {
        this.onChange(numericValue);
      } else {
        this.onChange(null);
      }
    } else {
      this.onChange(null);
    }
  }

  public formatWithCommas(value: string): string {
    if (!value) return '';

    // Ensure only valid numeric input is processed
    const validNumberPattern = /^-?\d*\.?\d*$/;
    if (!validNumberPattern.test(value)) {
      return this.formattedValue; // Return last valid formatted value
    }

    // Split integer and decimal parts
    const parts = value.split('.');
    let integerPart = parts[0].replace(/[^0-9-]/g, '');
    const decimalPart = parts[1] ? '.' + parts[1].replace(/[^0-9]/g, '') : '';

    // Prevent multiple minus signs
    if (integerPart.startsWith('-')) {
      integerPart = '-' + integerPart.replace(/-/g, '');
    } else {
      integerPart = integerPart.replace(/-/g, '');
    }

    // Add commas to integer part
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return integerPart + decimalPart;
  }

  onFocus(): void {
    this.showSteppers = true;
  }

  onBlur(): void {
    if (this.isButtonClicked) {
      // Skip blur handling if a button was clicked
      this.isButtonClicked = false;
      return;
    }
    this.showSteppers = false;
    this.onTouched();
  }

  handleButtonMouseDown(event: MouseEvent): void {
    event.preventDefault(); // Prevent the default behavior that might cause blur
    this.isButtonClicked = true; // Set flag to skip blur handling
  }

  increment(event: any): void {
    event.preventDefault();
    if (this.disabled || this.isHeader) return;

    let numericValue = this.rawValue ? parseFloat(this.rawValue) : 0;
    if (isNaN(numericValue)) numericValue = 0;
    numericValue += this.step;
    this.rawValue = numericValue.toString();
    this.formattedValue = this.formatWithCommas(this.rawValue);
    this.onChange(numericValue);
    this.onTouched();
    this.isButtonClicked = false;
    this.numberInput.nativeElement.focus();
  }

  decrement(event: any): void {
    event.preventDefault();
    if (this.disabled || this.isHeader) return;

    let numericValue = this.rawValue ? parseFloat(this.rawValue) : 0;
    if (isNaN(numericValue)) numericValue = 0;
    numericValue -= this.step;
    this.rawValue = numericValue.toString();
    this.formattedValue = this.formatWithCommas(this.rawValue);
    this.onChange(numericValue);
    this.onTouched();
    this.isButtonClicked = false;
    this.numberInput.nativeElement.focus();
  }
}
